import { useACLContext } from 'src/providers/ACLProvider/ACL.hook';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { ePlanCode } from 'src/providers/SubscriptionProvider/Subscription.enums';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { useWalletFlag } from './useWalletFlag';

/**
 * RET-2339. The plan codes that do not include WhatsApp API (CB-02). Básico
 * is `PLAN_BASIC` — `ACL.hook` calls it `isBusinessPlan`, so "Business" is
 * the same plan, not a third one. Legacy is `PLAN_LEGACY`. A plan code
 * outside this set (Profesional, Avanzado, or one this front does not know)
 * is never "known without access".
 */
const PLAN_CODES_WITHOUT_WAPI: ReadonlySet<string> = new Set<string>([
  ePlanCode.PLAN_BASIC,
  ePlanCode.PLAN_LEGACY,
]);

export type tWalletSurfaceAccess = {
  /** S1/S2/S3 montan. S4 pinta `BalanceTab` cuando esto es `true`. */
  isWalletSurfaceVisible: boolean;
  /** Cohorte resuelta y OFF: las superficies de créditos, como hoy. */
  isCreditsCohort: boolean;
  /** S4: hay suficiente para decidir algo (no necesariamente Saldo). */
  isWalletRouteDecidable: boolean;
  /**
   * RET-2339. Evidencia positiva de que ESTE workspace no tiene WhatsApp API:
   * plan resuelto y de cliente, plan Básico o Legacy, y la excepción de WBS
   * cargada para este workspace y en falso. No lee la bandera. `false` en
   * cualquier caso sin resolver: decide la leyenda de solo lectura y el
   * bloqueo de Campañas, que fallan cerrados si se equivocan.
   */
  isKnownWithoutWapiAccess: boolean;
};

/**
 * RET-2325. `useWalletFlag().isWalletEnabled` used to mean "has WhatsApp
 * API" because only WAPI customers carried `billing_wapi-wallet`. Since the
 * 30-sep sweep almost the whole base has it — it now means "pays with
 * balance, not credits" (MI-20) — and a Básico/Legacy workspace (Legacy is
 * the old Básico, product decision 2-oct) saw a balance it has no WhatsApp
 * API to spend. CB-02: WAPI is Profesional/Avanzado-only.
 *
 * The ONLY derivation of the compound predicate the wallet surfaces need.
 * Lives in the wallet slice, not inside `useWalletFlag` — that hook's own
 * docblock asks not to widen its re-render radius, and its other consumers
 * (send gates) must not start depending on ACL or subscription.
 *
 * Returns DECISIONS, not the raw signals they are built from. A caller that
 * re-derives its own compound off `hasWhatsAppApiAccess` or `isWalletEnabled`
 * can silently drift from the other surfaces — that is exactly what S4 did
 * (round 1 of this fix routed on the raw access predicate and landed a
 * never-paid workspace on `BalanceTab` through its previous workspace's
 * stale answer; see the round-2 regression tests, `*.ret2325.v2.test.tsx`).
 * A consumer that needs the raw cohort flag for something unrelated to
 * wallet surfaces (`GeneralSection`'s pre-existing one-time-credits funnel
 * gate, which predates the wallet and isn't part of this predicate) reads
 * `useWalletFlag` directly instead of asking this hook to carry it.
 *
 * WHY ACCESS ONLY COUNTS ONCE `hasSubscriptionData` IS TRUE. The access
 * predicate, `canUserAccessWhatsAppApi()`, answers off `subscription` in
 * `SubscriptionProvider`, and that context does not clear `subscription` on
 * a workspace switch (RET-1908) — it keeps answering for the PREVIOUS
 * workspace until this one's summary lands, and for an inactive workspace
 * that summary never lands at all (`Subscription.hook.ts:148-155`). So the
 * access predicate alone can read `true` for a workspace that has no plan
 * data of its own yet. `hasSubscriptionData` is the one signal that actually
 * resets on a switch (`Subscription.hook.ts:118`), which is why the
 * visibility decision is gated on it rather than trusting the access
 * predicate by itself. It covers the plan half only. The WBS exception half
 * (`canSeeWapiIntegration`, `WorkspaceProvider`) still keeps its previous
 * value across a switch, but since RET-2339 the provider discards a late
 * response from another workspace and publishes
 * `hasLoadedWapiAccessException`, true only once the exception RESPONDED for
 * the current workspace. `isKnownWithoutWapiAccess` requires that signal.
 * `isWalletSurfaceVisible` does not read it (out of RET-2339's scope): a
 * stale exception `true` from the previous workspace can still show the
 * balance until this workspace's exception responds, or for the session if
 * that request fails. That gap fails toward showing, not toward locking.
 * `isConfirmedNonClient` only unblocks the ROUTER (`isWalletRouteDecidable`) so it can stop returning `null`; it never
 * grants access — a workspace that never paid has nothing to see here either
 * way.
 *
 * WHY `isKnownWithoutWapiAccess` DOES NOT USE `!canUserAccessWhatsAppApi()`.
 * That predicate is also `false` for a Profesional/Avanzado whose summary
 * lacks the `WHATSAPP_API_ACCESS` row, for a plan code this front does not
 * know, and before the exception loads. Its negation would lock a paying
 * customer out. "Without access" is therefore built from positive evidence
 * only: a resolved plan whose code excludes WhatsApp API, plus a loaded
 * exception that says no. It does not read the wallet flag: a credits-cohort
 * Básico has no WhatsApp API either. It also requires `isClient` (ACTIVE,
 * TRIAL, PAST_DUE or SCHEDULED_CANCELLATION): a non-client or a PAUSED
 * subscription never gets the verdict, even if its summary still carries a
 * Básico/Legacy plan code — nothing guarantees it does not.
 */
export function useWalletSurfaceAccess(): tWalletSurfaceAccess {
  const { isWalletEnabled, hasLoadedWalletFlag } = useWalletFlag();
  const { canUserAccessWhatsAppApi } = useACLContext();
  const { subscription, hasSubscriptionData, isConfirmedNonClient, isClient } =
    useSubscriptionContext();
  const { canSeeWapiIntegration, hasLoadedWapiAccessException } =
    useWorkspaceContext();

  const hasWhatsAppApiAccess = canUserAccessWhatsAppApi();
  const isPlanResolved = hasSubscriptionData || isConfirmedNonClient;

  // S1/S2/S3: the surface shows only once the cohort, the plan and WAPI
  // access all say yes for THIS workspace. Failing closed on any of the four.
  const isWalletSurfaceVisible =
    hasLoadedWalletFlag &&
    isWalletEnabled &&
    hasSubscriptionData &&
    hasWhatsAppApiAccess;

  // Cohort settled and OFF: the credits surfaces keep their pre-wallet
  // behaviour — they never wait on the plan (decision 5). One deliberate
  // deviation: `CreditsCard` in S2 (`GeneralSection`) used to paint on
  // `!isWalletEnabled` alone, so it also showed while the flag was still
  // loading (defaults read `false`). Through this predicate it now waits for
  // the cohort to settle, as the Créditos tab and the router already did
  // since RET-2105 — a frame of the wrong cohort's card is not worth keeping.
  const isCreditsCohort = hasLoadedWalletFlag && !isWalletEnabled;

  // S4 (router): the decision can be made once the cohort has settled and,
  // only when it is ON, the plan has too (resolved, or a confirmed
  // non-client who will never get a summary). Being "decidable" only means
  // the router may stop returning `null` — it still has to pick between
  // `isWalletSurfaceVisible` and the fallback, never the raw access
  // predicate, so a decidable-by-`isConfirmedNonClient` workspace with a
  // stale `true` access answer still lands on the fallback.
  const isWalletRouteDecidable =
    hasLoadedWalletFlag && (!isWalletEnabled || isPlanResolved);

  // RET-1908: the plan code only counts once THIS workspace's summary landed.
  // `isClient` keeps a non-client and a paused subscription out (D4): their
  // summary may still carry a Básico/Legacy code, and this verdict locks.
  const isPlanWithoutWapi =
    hasSubscriptionData &&
    isClient === true &&
    PLAN_CODES_WITHOUT_WAPI.has(subscription?.planCode ?? '');

  const isKnownWithoutWapiAccess =
    isPlanWithoutWapi &&
    hasLoadedWapiAccessException === true &&
    !canSeeWapiIntegration;

  return {
    isWalletSurfaceVisible,
    isCreditsCohort,
    isWalletRouteDecidable,
    isKnownWithoutWapiAccess,
  };
}
